import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styles from './ImageCarousel.module.scss';
const IMAGE_DIMENSION = 340;
const THUMBNAIL_SIZE = 80;
const CONTAINER_PADDING = 40;
export const ImageCarousel = ({ images, excludeMainFromThumbnails = false, showImageCounter = false, onImageChange, }) => {
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const [loadingStates, setLoadingStates] = React.useState({});
    const [errorStates, setErrorStates] = React.useState({});
    const [showLeftArrow, setShowLeftArrow] = React.useState(false);
    const [showRightArrow, setShowRightArrow] = React.useState(false);
    const thumbnailContainerRef = React.useRef(null);
    const navigateToImage = React.useCallback((index) => {
        setSelectedIndex(index);
        onImageChange?.(index);
    }, [onImageChange]);
    const handleThumbnailClick = (index) => {
        navigateToImage(index);
    };
    React.useEffect(() => {
        if (thumbnailContainerRef.current && !excludeMainFromThumbnails) {
            const container = thumbnailContainerRef.current;
            const selectedButton = container.querySelector(`[data-thumbnail-index="${selectedIndex}"]`);
            if (selectedButton) {
                const containerWidth = container.clientWidth;
                const buttonLeft = selectedButton.offsetLeft;
                const buttonWidth = selectedButton.offsetWidth;
                const scrollLeft = container.scrollLeft;
                if (buttonLeft < scrollLeft ||
                    buttonLeft + buttonWidth > scrollLeft + containerWidth) {
                    const targetScroll = buttonLeft - containerWidth / 2 + buttonWidth / 2;
                    container.scrollTo({ left: targetScroll, behavior: 'smooth' });
                }
            }
        }
    }, [selectedIndex, excludeMainFromThumbnails]);
    const handleImageLoad = (index) => {
        setLoadingStates((prev) => ({ ...prev, [index]: false }));
    };
    const handleImageError = (index) => {
        setErrorStates((prev) => ({ ...prev, [index]: true }));
        setLoadingStates((prev) => ({ ...prev, [index]: false }));
    };
    const checkArrowVisibility = React.useCallback(() => {
        if (!thumbnailContainerRef.current)
            return;
        const container = thumbnailContainerRef.current;
        const { scrollLeft, scrollWidth, clientWidth } = container;
        setShowLeftArrow(scrollLeft > 0);
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }, []);
    const handleArrowClick = React.useCallback((direction) => {
        if (!thumbnailContainerRef.current)
            return;
        const container = thumbnailContainerRef.current;
        const scrollAmount = THUMBNAIL_SIZE + 8;
        const currentScroll = container.scrollLeft;
        const targetScroll = direction === 'left'
            ? Math.max(0, currentScroll - scrollAmount * 3)
            : currentScroll + scrollAmount * 3;
        container.scrollTo({
            left: targetScroll,
            behavior: 'smooth',
        });
    }, []);
    React.useEffect(() => {
        checkArrowVisibility();
        const container = thumbnailContainerRef.current;
        if (!container)
            return;
        const handleScroll = () => checkArrowVisibility();
        container.addEventListener('scroll', handleScroll);
        const handleResize = () => checkArrowVisibility();
        window.addEventListener('resize', handleResize);
        return () => {
            container.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [checkArrowVisibility, images.length]);
    React.useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                const newIndex = selectedIndex > 0 ? selectedIndex - 1 : images.length - 1;
                navigateToImage(newIndex);
            }
            else if (e.key === 'ArrowRight') {
                e.preventDefault();
                const newIndex = selectedIndex < images.length - 1 ? selectedIndex + 1 : 0;
                navigateToImage(newIndex);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedIndex, images.length, navigateToImage]);
    React.useEffect(() => {
        setLoadingStates((prev) => {
            if (prev[selectedIndex] !== undefined)
                return prev;
            return { ...prev, [selectedIndex]: true };
        });
    }, [selectedIndex]);
    if (!images.length) {
        return null;
    }
    const selectedImage = images[selectedIndex] || images[0];
    const isLoading = loadingStates[selectedIndex] !== false;
    const hasError = errorStates[selectedIndex] === true;
    const containerSize = `${IMAGE_DIMENSION + CONTAINER_PADDING}px`;
    return (_jsxs("div", { className: styles.imageCarousel, children: [_jsxs("div", { className: styles.mainImageContainer, style: { height: containerSize, width: containerSize }, children: [isLoading && !hasError && (_jsx("div", { className: styles.loadingSpinner, children: _jsx("div", { className: styles.spinner }) })), hasError ? (_jsxs("div", { className: styles.errorState, children: [_jsx("div", { className: styles.errorIcon, children: "\u26A0\uFE0F" }), _jsx("p", { className: styles.errorText, children: "Failed to load image" })] })) : (_jsx("img", { src: selectedImage.src, alt: selectedImage.alt, className: styles.mainImage, loading: "lazy", onLoad: () => handleImageLoad(selectedIndex), onError: () => handleImageError(selectedIndex), style: { opacity: isLoading ? 0 : 1 } })), showImageCounter && images.length > 1 && (_jsxs("div", { className: styles.imageCounter, children: [selectedIndex + 1, " / ", images.length] }))] }), images.length > 1 && (_jsxs("div", { className: styles.thumbnailWrapper, style: { maxWidth: containerSize }, children: [showLeftArrow ? (_jsx("button", { type: "button", className: styles.carouselArrow, onClick: () => handleArrowClick('left'), "aria-label": "Scroll thumbnails left", children: _jsx("span", { className: styles.arrowIcon, children: "\u2039" }) })) : (_jsx("div", { className: styles.arrowPlaceholder })), _jsx("div", { ref: thumbnailContainerRef, className: styles.thumbnailContainer, children: images.map((image, index) => {
                            if (excludeMainFromThumbnails && index === selectedIndex) {
                                return null;
                            }
                            const isActive = index === selectedIndex;
                            const buttonClasses = [
                                styles.thumbnailButton,
                                isActive ? styles.thumbnailButtonActive : '',
                            ]
                                .filter(Boolean)
                                .join(' ');
                            return (_jsx("button", { type: "button", className: buttonClasses, style: { width: `${THUMBNAIL_SIZE}px`, height: `${THUMBNAIL_SIZE}px` }, onClick: () => handleThumbnailClick(index), "data-thumbnail-index": index, "aria-label": `View ${image.alt}`, children: _jsx("img", { src: image.thumbnailSrc || image.src, alt: image.alt, className: styles.thumbnailImage }) }, index));
                        }) }), showRightArrow ? (_jsx("button", { type: "button", className: styles.carouselArrow, onClick: () => handleArrowClick('right'), "aria-label": "Scroll thumbnails right", children: _jsx("span", { className: styles.arrowIcon, children: "\u203A" }) })) : (_jsx("div", { className: styles.arrowPlaceholder }))] }))] }));
};
